import { InfoCircledIcon } from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { Popover } from 'radix-ui';
import React, { useMemo } from 'react';

import { getServerBuildInfo } from '~/apis/apiBuildInfo';
import { useTranslation } from '~/app';
import { cn } from '~/utils/styles';

const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

const VersionInfoPopover = (): JSX.Element => {
  const { t, locale } = useTranslation();

  const { data: serverBuildInfo, isError } = useQuery({
    queryKey: ['buildinfo'],
    queryFn: getServerBuildInfo,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const clientDateRaw = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : '';
  const clientVersion = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : '—';
  const clientDateFormatted = useMemo(
    () => (clientDateRaw ? new Date(clientDateRaw).toLocaleString(locale, dateOptions) : '—'),
    [locale, clientDateRaw],
  );

  const serverVersion = serverBuildInfo?.version ?? '—';
  const serverDateFormatted =
    isError || !serverBuildInfo?.date
      ? t('version_info_unavailable')
      : (() => {
          try {
            return new Date(serverBuildInfo.date).toLocaleString(locale, dateOptions);
          } catch {
            return serverBuildInfo.date;
          }
        })();

  return (
    <Popover.Root>
      <Popover.Trigger
        type="button"
        aria-label={t('version_info_button_aria')}
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-md text-white',
          'hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
        )}
      >
        <InfoCircledIcon className="size-5" aria-hidden />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={8}
          className={cn(
            'z-50 max-w-xs rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-900 shadow-lg',
            'focus:outline-none',
          )}
        >
          <div className="grid gap-2">
            <div>
              <div className="font-medium text-gray-700">{t('version_info_client')}</div>
              <div className="mt-0.5 text-gray-600">
                {t('website_updatedAt')}: {clientDateFormatted}
              </div>
              <div className="text-gray-600">
                {t('version_info_version')}: {clientVersion}
              </div>
            </div>
            <div className="border-t border-gray-100 pt-2">
              <div className="font-medium text-gray-700">{t('version_info_server')}</div>
              <div className="mt-0.5 text-gray-600">
                {t('website_updatedAt')}: {serverDateFormatted}
              </div>
              <div className="text-gray-600">
                {t('version_info_version')}:{' '}
                {isError ? t('version_info_unavailable') : serverVersion}
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default VersionInfoPopover;
